<script setup>
// Shared breadcrumb trail — semantic <nav><ol> markup + schema.org
// BreadcrumbList JSON-LD (SEO rich results). Single source of truth for every
// page that shows breadcrumbs (content-page hero + the detail views).
//
// items: [{ label, to? }] — the last crumb usually omits `to` (current page),
// rendering as an aria-current span rather than a link.
// onBand: swaps colours to read on the lime hero band (ContentHero) instead of
// the default cream page background.
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { SITE_URL, withTrailingSlash } from '@/utils/siteUrl'

const props = defineProps({
  items:  { type: Array,   default: () => [] },   // [{ label, to? }]
  onBand: { type: Boolean, default: false },
})

const route = useRoute()
const baseUrl = import.meta.env.VITE_APP_BASE_URL || SITE_URL

// schema.org BreadcrumbList — absolute URLs, positions sequential from 1. The
// current crumb (no `to`) resolves to the current route path.
const jsonLd = computed(() => JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: props.items.map((crumb, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: crumb.label,
    item: `${baseUrl}${withTrailingSlash(crumb.to || route.path)}`,
  })),
}))

useHead(computed(() => (
  props.items.length
    ? { script: [{ type: 'application/ld+json', innerHTML: jsonLd.value }] }
    : {}
)))
</script>

<template>
  <nav v-if="items.length" class="breadcrumbs" :class="{ 'on-band': onBand }" aria-label="Breadcrumb">
    <ol>
      <li v-for="(crumb, i) in items" :key="i">
        <RouterLink v-if="crumb.to" :to="withTrailingSlash(crumb.to)" class="crumb">{{ crumb.label }}</RouterLink>
        <span v-else class="crumb current" aria-current="page">{{ crumb.label }}</span>
      </li>
    </ol>
  </nav>
</template>

<style lang="scss" scoped>
.breadcrumbs {
  ol {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-2;
    list-style: none;
    margin: 0;
    padding: 0;
    font-size: $font-size-sm;
  }

  li {
    display: flex;
    align-items: center;
    gap: $space-2;

    // Decorative separator between crumbs — not part of the list-item text, so
    // it isn't announced by assistive tech.
    &:not(:last-child)::after {
      content: '›';
      color: var(--color-text-muted);
      opacity: .7;
    }
  }

  .crumb {
    color: var(--color-text-muted);
    text-decoration: none;
    &:hover { text-decoration: underline; }
    &:focus-visible { @include focus-ring; outline: none; }
  }

  // Current page — emphasized but not a link, so no underline (the real links
  // above underline on hover only; underlining the non-link would misread as
  // clickable).
  .crumb.current {
    color: var(--color-ink);
    font-weight: 500;
  }

  // Hero band (ContentHero) — inherit the band's own text colour so crumbs read
  // correctly on light fills (lime/pink/yellow/cream → ink) AND dark fills
  // (green/blue → the light on-banner colour the band sets). Using a fixed ink
  // here would be unreadable on the dark bands.
  &.on-band {
    .crumb, .crumb.current     { color: inherit; }
    li:not(:last-child)::after { color: inherit; opacity: .6; }
  }
}
</style>
